import { z } from "zod";
import type { BrandVisualUniverseImageSection } from "./generated-content.ts";
import { typographyStyleClaimSchema } from "./typography.ts";

export const BRAND_ARCHETYPES = [
  "The Innocent",
  "The Everyman",
  "The Hero",
  "The Outlaw",
  "The Explorer",
  "The Creator",
  "The Ruler",
  "The Magician",
  "The Lover",
  "The Caregiver",
  "The Jester",
  "The Sage",
] as const;

export type BrandArchetype = (typeof BRAND_ARCHETYPES)[number];

export const BRAND_COMMUNICATION_TONES = [
  "formal",
  "friendly",
  "witty",
  "authoritative",
  "playful",
  "analytical",
  "empathetic",
  "mixed",
] as const;

export type BrandCommunicationTone = (typeof BRAND_COMMUNICATION_TONES)[number];

export const BRAND_VOICE_ATTRIBUTES = [
  "confident",
  "playful",
  "analytical",
  "empathetic",
  "professional",
  "approachable",
  "bold",
  "mixed",
] as const;

export type BrandVoiceAttribute = (typeof BRAND_VOICE_ATTRIBUTES)[number];

export const BRAND_VOICE_ARCHETYPE_MAP = {
  confident: "The Hero",
  playful: "The Jester",
  analytical: "The Sage",
  empathetic: "The Caregiver",
  professional: "The Ruler",
  approachable: "The Everyman",
  bold: "The Outlaw",
  mixed: null,
} as const satisfies Record<BrandVoiceAttribute, BrandArchetype | null>;

export const getBrandArchetypeForVoice = (voice: unknown): BrandArchetype | null => {
  const parsed = z.enum(BRAND_VOICE_ATTRIBUTES).safeParse(voice);
  if (!parsed.success) {
    return null;
  }

  return BRAND_VOICE_ARCHETYPE_MAP[parsed.data];
};

export const BRAND_VOCABULARY_STYLES = [
  "simple",
  "technical",
  "trendy",
  "aspirational",
  "mixed",
] as const;

export type BrandVocabularyStyle = (typeof BRAND_VOCABULARY_STYLES)[number];

export const BRAND_SENTENCE_COMPLEXITIES = ["simple", "moderate", "complex", "mixed"] as const;

export type BrandSentenceComplexity = (typeof BRAND_SENTENCE_COMPLEXITIES)[number];

export const BRAND_EMOJI_USAGES = ["none", "rare", "occasional", "frequent"] as const;

export type BrandEmojiUsage = (typeof BRAND_EMOJI_USAGES)[number];

export const BRAND_CULTURAL_REFERENCE_LEVELS = ["none", "light", "moderate", "high"] as const;

export type BrandCulturalReferenceLevel = (typeof BRAND_CULTURAL_REFERENCE_LEVELS)[number];

export const BRAND_SOCIAL_CONTENT_STYLES = [
  "social_entertainment",
  "expert_authority",
  "aspirational_lifestyle",
  "utility_education",
  "community_led",
] as const;

export type BrandSocialContentStyle = (typeof BRAND_SOCIAL_CONTENT_STYLES)[number];
export const brandSocialContentStyleSchema = z.enum(BRAND_SOCIAL_CONTENT_STYLES);

export const BRAND_SOCIAL_MECHANICS = [
  "pov",
  "comparison",
  "confession",
  "unpopular_opinion",
  "relatable_moment",
  "mini_drama",
  "roast",
  "checklist",
  "meme",
  "text_conversation",
  "before_after",
  "mistake",
  "expectation_vs_reality",
  "things_people_say",
  "saveable_rule",
  "contrarian_take",
  "framework",
  "case_study",
  "tutorial",
  "community_debate",
] as const;

export type BrandSocialMechanic = (typeof BRAND_SOCIAL_MECHANICS)[number];
export const brandSocialMechanicSchema = z.enum(BRAND_SOCIAL_MECHANICS);
export const BRAND_SOCIAL_MECHANIC_SELECTION_LIMIT = 10;

export const BALANCED_GENERATION_SOCIAL_MECHANICS = [
  "relatable_moment",
  "saveable_rule",
  "before_after",
  "mistake",
  "case_study",
  "checklist",
  "framework",
  "things_people_say",
  "contrarian_take",
  "comparison",
  "tutorial",
  "expectation_vs_reality",
  "community_debate",
  "confession",
  "mini_drama",
  "text_conversation",
  "unpopular_opinion",
  "pov",
  "meme",
  "roast",
] as const satisfies readonly BrandSocialMechanic[];
export const brandGenerationSocialMechanicSchema = z.enum(BALANCED_GENERATION_SOCIAL_MECHANICS);

const BRAND_SOCIAL_MECHANIC_SET = new Set<string>(BRAND_SOCIAL_MECHANICS);

export const isBrandSocialMechanic = (value: unknown): value is BrandSocialMechanic =>
  typeof value === "string" && BRAND_SOCIAL_MECHANIC_SET.has(value);

const EXPLICIT_LITERAL_POV_LANGUAGE_PATTERNS = [
  /\bphone[-\s]?pov\b/iu,
  /\bpov\b/iu,
  /\bfirst[-\s]?person(?:\s+(?:camera|viewpoint|view|framing|frame|perspective|shot|angle))?\b/iu,
  /\bviewer[-\s]?as[-\s]participant\b/iu,
  /\bthrough\s+(?:your|my|their|the\s+viewer'?s)\s+eyes\b/iu,
  /\bfrom\s+(?:your|my|their|the\s+viewer'?s)\s+(?:pov|perspective|view)\b/iu,
] as const;

export const hasExplicitLiteralPovLanguage = (value: unknown): boolean => {
  if (typeof value === "string") {
    return EXPLICIT_LITERAL_POV_LANGUAGE_PATTERNS.some((pattern) => pattern.test(value));
  }

  if (Array.isArray(value)) {
    return value.some(hasExplicitLiteralPovLanguage);
  }

  if (value && typeof value === "object") {
    return Object.values(value).some(hasExplicitLiteralPovLanguage);
  }

  return false;
};

export const shouldAllowLiteralPov = (input: {
  plannedMechanic?: unknown;
  sourceTexts?: readonly unknown[];
}): boolean =>
  input.plannedMechanic === "pov" ||
  Boolean(input.sourceTexts?.some(hasExplicitLiteralPovLanguage));

const POV_LIKE_FRAMING_LANGUAGE_PATTERNS = [
  /\bselfie(?:[-\s]style|[-\s]framing|[-\s]angle|[-\s]shot| shot| framing| angle)?\b/iu,
  /\bfront[-\s]?camera\b/iu,
  /\bfrom\s+(?:your|my|our|their|the\s+viewer'?s)\s+(?:phone|camera|hand|hands)\b/iu,
  /\b(?:your|my|our|their|the\s+viewer'?s)\s+phone\s+(?:camera|screen|view)\b/iu,
  /\b(?:your|my|our|their|the\s+viewer'?s)\s+hands?\s+(?:holding|typing|tapping|grabbing|reaching|placing|opening|scrolling|selecting|shooting|using)\b/iu,
  /\b(?:holding|typing|tapping|grabbing|reaching|placing|opening|scrolling|selecting|shooting|using)\s+(?:with|from)\s+(?:your|my|our|their|the\s+viewer'?s)\s+hands?\b/iu,
  /\bphone[-\s]?in[-\s]?hand\b/iu,
  /\bhandheld\s+phone\s+view\b/iu,
  /\bthrough\s+(?:your|my|our|their|the\s+viewer'?s)\s+(?:phone|camera|screen)\b/iu,
  /\bfirst[-\s]?person\s+hands?\b/iu,
  /\bhands[-\s]?as[-\s]?the[-\s]?viewer\b/iu,
] as const;

export const hasPovLikeFramingLanguage = (value: unknown): boolean => {
  if (hasExplicitLiteralPovLanguage(value)) {
    return true;
  }

  if (typeof value === "string") {
    return POV_LIKE_FRAMING_LANGUAGE_PATTERNS.some((pattern) => pattern.test(value));
  }

  if (Array.isArray(value)) {
    return value.some(hasPovLikeFramingLanguage);
  }

  if (value && typeof value === "object") {
    return Object.values(value).some(hasPovLikeFramingLanguage);
  }

  return false;
};

export const NON_POV_GENERATION_VISUAL_PERSPECTIVE_ROLES = [
  "observational_scene",
  "artifact_flat_lay",
  "detail_close_up",
  "over_the_shoulder",
  "wide_environmental",
  "product_artifact",
  "hands_in_action",
  "graphic_minimal_reference",
] as const;

export const GENERATION_VISUAL_PERSPECTIVE_ROLES = [
  ...NON_POV_GENERATION_VISUAL_PERSPECTIVE_ROLES,
  "pov",
] as const;

export type GenerationVisualPerspectiveRole = (typeof GENERATION_VISUAL_PERSPECTIVE_ROLES)[number];

export const GENERATION_VISUAL_PERSPECTIVE_ROLE_DETAILS = {
  observational_scene: {
    label: "observational third-person scene",
    instruction:
      "Use a documentary third-person camera watching the moment from outside the participant's body; no viewer hands, selfie posture, or phone-in-hand camera.",
  },
  artifact_flat_lay: {
    label: "artifact flat-lay",
    instruction:
      "Use a top-down or overhead arrangement of objects, notes, receipts, tools, packaging, or proof artifacts as the main story surface.",
  },
  detail_close_up: {
    label: "detail close-up",
    instruction:
      "Use a tight crop on one tactile detail, object interaction, expression cue, material, or proof point without implying the viewer is holding the camera.",
  },
  over_the_shoulder: {
    label: "over-the-shoulder",
    instruction:
      "Use an outside observer camera behind or beside a generic fictional person, looking past their shoulder at the scene or artifact.",
  },
  wide_environmental: {
    label: "wide/environmental",
    instruction:
      "Use a wider scene where setting, surfaces, light, and spatial context carry the idea; keep the subject grounded in the environment.",
  },
  product_artifact: {
    label: "product/artifact",
    instruction:
      "Use a product, package, tool, document, physical proof, or non-readable artifact as the visual lead rather than a first-person camera setup.",
  },
  hands_in_action: {
    label: "hands-in-action",
    instruction:
      "Show hands as visible subjects within an observed scene performing an action; the hands must not read as the viewer's own first-person hands.",
  },
  graphic_minimal_reference: {
    label: "graphic/minimal reference",
    instruction:
      "Use a sparse, graphic, object-led or abstract composition with strong geometry and negative space, not a creator-camera scene.",
  },
  pov: {
    label: "literal POV / first-person",
    instruction:
      "Use first-person or viewer-participant framing only when the current request explicitly asks for it.",
  },
} as const satisfies Record<
  GenerationVisualPerspectiveRole,
  { label: string; instruction: string }
>;

const visualPerspectiveRolePatterns = [
  {
    role: "artifact_flat_lay",
    patterns: [/\bflat[-\s]?lay\b/iu, /\boverhead\b/iu, /\btop[-\s]?down\b/iu],
  },
  {
    role: "detail_close_up",
    patterns: [/\bclose[-\s]?up\b/iu, /\bmacro\b/iu, /\bdetail\b/iu, /\btight crop\b/iu],
  },
  {
    role: "over_the_shoulder",
    patterns: [/\bover[-\s]?the[-\s]?shoulder\b/iu, /\bbehind\s+(?:a|the)\s+shoulder\b/iu],
  },
  {
    role: "wide_environmental",
    patterns: [/\bwide\b/iu, /\benvironmental\b/iu, /\bspatial\b/iu, /\broom\b/iu],
  },
  {
    role: "product_artifact",
    patterns: [
      /\bproduct[-\s]?led\b/iu,
      /\bproduct\b/iu,
      /\bartifact\b/iu,
      /\bpackage\b/iu,
      /\bproof[-\s]?led\b/iu,
    ],
  },
  {
    role: "hands_in_action",
    patterns: [/\bhands[-\s]?in[-\s]?action\b/iu, /\bhands?\b/iu, /\bgesture\b/iu],
  },
  {
    role: "graphic_minimal_reference",
    patterns: [/\bgraphic\b/iu, /\bminimal\b/iu, /\babstract\b/iu, /\bgeometry\b/iu],
  },
  {
    role: "observational_scene",
    patterns: [
      /\bobservational\b/iu,
      /\bthird[-\s]?person\b/iu,
      /\bdocumentary\b/iu,
      /\bcandid\b/iu,
      /\bscene\b/iu,
    ],
  },
] as const satisfies Array<{
  role: Exclude<GenerationVisualPerspectiveRole, "pov">;
  patterns: readonly RegExp[];
}>;

const collectGenerationPerspectiveText = (value: unknown): string => {
  if (typeof value === "string") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(collectGenerationPerspectiveText).filter(Boolean).join("\n");
  }

  if (value && typeof value === "object") {
    return Object.values(value).map(collectGenerationPerspectiveText).filter(Boolean).join("\n");
  }

  return "";
};

export const inferGenerationVisualPerspectiveRole = (
  value: unknown,
): GenerationVisualPerspectiveRole | null => {
  if (hasPovLikeFramingLanguage(value)) {
    return "pov";
  }

  const text = collectGenerationPerspectiveText(value);
  if (!text.trim()) {
    return null;
  }

  for (const { role, patterns } of visualPerspectiveRolePatterns) {
    if (patterns.some((pattern) => pattern.test(text))) {
      return role;
    }
  }

  return null;
};

export const getGenerationVisualPerspectiveRoleLabel = (
  role: GenerationVisualPerspectiveRole,
): string => GENERATION_VISUAL_PERSPECTIVE_ROLE_DETAILS[role].label;

export const formatGenerationVisualPerspectiveRoleDirective = (
  role: GenerationVisualPerspectiveRole,
): string =>
  `${GENERATION_VISUAL_PERSPECTIVE_ROLE_DETAILS[role].label}: ${GENERATION_VISUAL_PERSPECTIVE_ROLE_DETAILS[role].instruction}`;

export const formatNonPovGenerationVisualPerspectiveRoleMenu = (): string =>
  NON_POV_GENERATION_VISUAL_PERSPECTIVE_ROLES.map(getGenerationVisualPerspectiveRoleLabel).join(
    ", ",
  );

export const getGenerationVisualPerspectiveRoleForIndex = (
  index = 0,
  options: { allowPov?: boolean } = {},
): GenerationVisualPerspectiveRole => {
  const roles = options.allowPov
    ? GENERATION_VISUAL_PERSPECTIVE_ROLES
    : NON_POV_GENERATION_VISUAL_PERSPECTIVE_ROLES;
  const safeIndex = Number.isFinite(index) ? Math.abs(Math.trunc(index)) : 0;
  return roles[safeIndex % roles.length]!;
};

export const resolveGenerationVisualPerspectiveRole = (
  input: {
    preferredText?: unknown;
    slotIndex?: number;
    variantIndex?: number;
    candidateIndex?: number;
    allowPov?: boolean;
  } = {},
): GenerationVisualPerspectiveRole => {
  const inferredRole = inferGenerationVisualPerspectiveRole(input.preferredText);
  if (inferredRole && (input.allowPov || inferredRole !== "pov")) {
    return inferredRole;
  }

  const slotOffset = input.slotIndex ?? 0;
  const variantOffset = input.variantIndex ? input.variantIndex - 1 : 0;
  const candidateOffset = input.candidateIndex ? input.candidateIndex - 1 : 0;

  return getGenerationVisualPerspectiveRoleForIndex(slotOffset + variantOffset + candidateOffset, {
    allowPov: input.allowPov,
  });
};

const toUniqueBrandSocialMechanics = (values: readonly unknown[] | undefined) => {
  const seen = new Set<BrandSocialMechanic>();
  for (const value of values ?? []) {
    if (isBrandSocialMechanic(value)) {
      seen.add(value);
    }
  }
  return seen;
};

export const getEffectiveGenerationSocialMechanics = (
  input: {
    preferredMechanics?: readonly unknown[];
    avoidedMechanics?: readonly unknown[];
    allowPov?: boolean;
    fallbackMechanics?: readonly BrandSocialMechanic[];
  } = {},
): BrandSocialMechanic[] => {
  const preferredMechanics = toUniqueBrandSocialMechanics(input.preferredMechanics);
  const avoidedMechanics = toUniqueBrandSocialMechanics(input.avoidedMechanics);
  const fallbackMechanics =
    input.fallbackMechanics && input.fallbackMechanics.length > 0
      ? input.fallbackMechanics
      : BALANCED_GENERATION_SOCIAL_MECHANICS;
  const sourceMechanics =
    preferredMechanics.size > 0
      ? BALANCED_GENERATION_SOCIAL_MECHANICS.filter((mechanic) => preferredMechanics.has(mechanic))
      : fallbackMechanics;
  const isAllowed = (mechanic: BrandSocialMechanic) =>
    !avoidedMechanics.has(mechanic) && (input.allowPov || mechanic !== "pov");
  const selectedMechanics = sourceMechanics.filter(isAllowed);

  if (selectedMechanics.length > 0 && selectedMechanics[0] !== "pov") {
    return selectedMechanics;
  }

  const fallbackSelectedMechanics = BALANCED_GENERATION_SOCIAL_MECHANICS.filter(isAllowed);
  return fallbackSelectedMechanics.length > 0 ? fallbackSelectedMechanics : selectedMechanics;
};

export const BRAND_SOCIAL_INTENSITY_LEVELS = ["low", "medium", "high"] as const;

export type BrandSocialIntensityLevel = (typeof BRAND_SOCIAL_INTENSITY_LEVELS)[number];

export const BRAND_SOCIAL_CONFIDENCE_LEVELS = ["low", "medium", "high"] as const;

export type BrandSocialConfidenceLevel = (typeof BRAND_SOCIAL_CONFIDENCE_LEVELS)[number];

export const BRAND_VOICE_CONTENT_DNA_FIELD_PATHS = [
  "brandVoiceCommunicationStyle.contentDnaPrimaryStyle",
  "brandVoiceCommunicationStyle.contentDnaSecondaryStyle",
  "brandVoiceCommunicationStyle.contentDnaConfidence",
  "brandVoiceCommunicationStyle.contentDnaHumorLevel",
  "brandVoiceCommunicationStyle.contentDnaOpinionatedness",
  "brandVoiceCommunicationStyle.contentDnaAspirationLevel",
  "brandVoiceCommunicationStyle.contentDnaEducationLevel",
  "brandVoiceCommunicationStyle.contentDnaCommunityLevel",
  "brandVoiceCommunicationStyle.socialMechanics",
  "brandVoiceCommunicationStyle.socialAvoidedMechanics",
  "brandVoiceCommunicationStyle.socialTruths",
  "brandVoiceCommunicationStyle.audienceTensions",
  "brandVoiceCommunicationStyle.shareabilityTriggers",
  "brandVoiceCommunicationStyle.contentAngles",
  "brandVoiceCommunicationStyle.contentAntiPatterns",
  "brandVoiceCommunicationStyle.contentDnaRationale",
] as const;

export type BrandVoiceContentDnaFieldPath = (typeof BRAND_VOICE_CONTENT_DNA_FIELD_PATHS)[number];

export const REQUIRED_BRAND_VOICE_CONTENT_DNA_FIELD_PATHS = [
  "brandVoiceCommunicationStyle.contentDnaPrimaryStyle",
  "brandVoiceCommunicationStyle.contentDnaConfidence",
  "brandVoiceCommunicationStyle.contentDnaHumorLevel",
  "brandVoiceCommunicationStyle.contentDnaOpinionatedness",
  "brandVoiceCommunicationStyle.contentDnaAspirationLevel",
  "brandVoiceCommunicationStyle.contentDnaEducationLevel",
  "brandVoiceCommunicationStyle.contentDnaCommunityLevel",
  "brandVoiceCommunicationStyle.contentDnaRationale",
] as const satisfies readonly BrandVoiceContentDnaFieldPath[];

const getDuplicateEnumValues = <T extends string>(values: readonly T[]): T[] => {
  const seen = new Set<T>();
  const duplicates = new Set<T>();

  for (const value of values) {
    if (seen.has(value)) {
      duplicates.add(value);
    }
    seen.add(value);
  }

  return [...duplicates];
};

const assertUniqueSocialMechanics = (
  values: readonly BrandSocialMechanic[],
  ctx: z.RefinementCtx,
  path: string,
) => {
  const duplicates = getDuplicateEnumValues(values);
  if (duplicates.length > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [path],
      message: `Social mechanics must be unique: ${duplicates.join(", ")}`,
    });
  }
};

const assertUniqueSocialStyles = (
  values: readonly BrandSocialContentStyle[],
  ctx: z.RefinementCtx,
  path: string,
) => {
  const duplicates = getDuplicateEnumValues(values);
  if (duplicates.length > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [path],
      message: `Social styles must be unique: ${duplicates.join(", ")}`,
    });
  }
};

export const brandSocialAffinitiesSchema = z
  .object({
    styles: z
      .array(brandSocialContentStyleSchema)
      .max(BRAND_SOCIAL_CONTENT_STYLES.length)
      .default([])
      .describe("Social content styles this creative structure naturally supports."),
    mechanics: z
      .array(brandSocialMechanicSchema)
      .max(BRAND_SOCIAL_MECHANICS.length)
      .default([])
      .describe("Platform-native mechanics this creative structure naturally supports."),
  })
  .strict()
  .superRefine((value, ctx) => {
    assertUniqueSocialStyles(value.styles, ctx, "styles");
    assertUniqueSocialMechanics(value.mechanics, ctx, "mechanics");
  });
export type BrandSocialAffinities = z.infer<typeof brandSocialAffinitiesSchema>;

const contentDnaTextSchema = z.string().trim().min(1).max(280);
const contentDnaTextArraySchema = z.array(contentDnaTextSchema).max(12).default([]);

const brandVoiceContentDnaFieldsShape = {
  contentDnaPrimaryStyle: brandSocialContentStyleSchema
    .nullable()
    .default(null)
    .describe("Primary Content DNA style the brand should optimize for on social."),
  contentDnaSecondaryStyle: brandSocialContentStyleSchema
    .nullable()
    .default(null)
    .describe("Optional secondary Content DNA style for variety without losing strategic fit."),
  contentDnaConfidence: z
    .enum(BRAND_SOCIAL_CONFIDENCE_LEVELS)
    .nullable()
    .default(null)
    .describe("Confidence in the selected Content DNA profile."),
  contentDnaHumorLevel: z
    .enum(BRAND_SOCIAL_INTENSITY_LEVELS)
    .nullable()
    .default(null)
    .describe("How much the brand should lean into humor in social-native content."),
  contentDnaOpinionatedness: z
    .enum(BRAND_SOCIAL_INTENSITY_LEVELS)
    .nullable()
    .default(null)
    .describe("How strongly the brand should take positions in social content."),
  contentDnaAspirationLevel: z
    .enum(BRAND_SOCIAL_INTENSITY_LEVELS)
    .nullable()
    .default(null)
    .describe("How much content should lean into identity, lifestyle, and transformation."),
  contentDnaEducationLevel: z
    .enum(BRAND_SOCIAL_INTENSITY_LEVELS)
    .nullable()
    .default(null)
    .describe("How much content should lean into practical teaching and saveability."),
  contentDnaCommunityLevel: z
    .enum(BRAND_SOCIAL_INTENSITY_LEVELS)
    .nullable()
    .default(null)
    .describe("How much content should invite belonging, participation, and shared experience."),
  socialMechanics: z
    .array(brandSocialMechanicSchema)
    .max(BRAND_SOCIAL_MECHANIC_SELECTION_LIMIT)
    .default([])
    .describe("Preferred platform-native mechanics that fit this brand's Content DNA."),
  socialAvoidedMechanics: z
    .array(brandSocialMechanicSchema)
    .max(BRAND_SOCIAL_MECHANIC_SELECTION_LIMIT)
    .default([])
    .describe("Platform-native mechanics that should usually be avoided for this brand."),
  socialTruths: contentDnaTextArraySchema.describe(
    "Relatable observations, cultural truths, or audience recognition moments the brand can own.",
  ),
  audienceTensions: contentDnaTextArraySchema.describe(
    "Human tensions, contradictions, frustrations, or desires that should power social hooks.",
  ),
  shareabilityTriggers: contentDnaTextArraySchema.describe(
    "Reasons someone would comment, tag a friend, laugh, disagree, or share.",
  ),
  contentAngles: contentDnaTextArraySchema.describe(
    "Editable social content angles generated from Human Lens and Domain Territory intersections.",
  ),
  contentAntiPatterns: contentDnaTextArraySchema.describe(
    "Off-brand content patterns, hooks, or formats the brand should avoid.",
  ),
  contentDnaRationale: z
    .string()
    .trim()
    .max(1200)
    .nullable()
    .default(null)
    .describe("Why this Content DNA fits the brand facts and audience."),
} as const;

type BrandVoiceContentDnaFieldsValue = z.infer<z.ZodObject<typeof brandVoiceContentDnaFieldsShape>>;

const assertBrandVoiceContentDnaFieldConsistency = (
  value: BrandVoiceContentDnaFieldsValue,
  ctx: z.RefinementCtx,
) => {
  if (
    value.contentDnaPrimaryStyle &&
    value.contentDnaSecondaryStyle &&
    value.contentDnaPrimaryStyle === value.contentDnaSecondaryStyle
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["contentDnaSecondaryStyle"],
      message: "Secondary Content DNA style must differ from primary style.",
    });
  }

  assertUniqueSocialMechanics(value.socialMechanics, ctx, "socialMechanics");
  assertUniqueSocialMechanics(value.socialAvoidedMechanics, ctx, "socialAvoidedMechanics");

  const preferred = new Set(value.socialMechanics);
  const overlaps = value.socialAvoidedMechanics.filter((mechanic) => preferred.has(mechanic));
  if (overlaps.length > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["socialAvoidedMechanics"],
      message: `Avoided mechanics cannot also be preferred: ${overlaps.join(", ")}`,
    });
  }
};

export const brandVoiceContentDnaFieldsSchema = z
  .object(brandVoiceContentDnaFieldsShape)
  .superRefine(assertBrandVoiceContentDnaFieldConsistency);
export type BrandVoiceContentDnaFields = z.infer<typeof brandVoiceContentDnaFieldsSchema>;

export const brandVoiceSocialExpressionSchema = z
  .object({
    primaryStyle: brandSocialContentStyleSchema.describe(
      "Primary social content style the brand should optimize for.",
    ),
    secondaryStyle: brandSocialContentStyleSchema
      .nullable()
      .default(null)
      .describe("Optional secondary social content style for variety."),
    confidence: z
      .enum(BRAND_SOCIAL_CONFIDENCE_LEVELS)
      .describe("Confidence in the selected social expression profile."),
    opinionatedness: z
      .enum(BRAND_SOCIAL_INTENSITY_LEVELS)
      .describe("How strongly the brand should take positions in social content."),
    aspirationLevel: z
      .enum(BRAND_SOCIAL_INTENSITY_LEVELS)
      .describe("How much the brand should lean into aspiration, identity, and transformation."),
    educationLevel: z
      .enum(BRAND_SOCIAL_INTENSITY_LEVELS)
      .describe("How much the brand should lean into practical teaching and saveable utility."),
    communityLevel: z
      .enum(BRAND_SOCIAL_INTENSITY_LEVELS)
      .describe(
        "How much the brand should lean into belonging, shared experiences, and audience participation.",
      ),
    preferredMechanics: z
      .array(brandSocialMechanicSchema)
      .min(1)
      .max(10)
      .describe("Platform-native mechanics that fit the brand voice best."),
    avoidedMechanics: z
      .array(brandSocialMechanicSchema)
      .max(10)
      .default([])
      .describe("Platform-native mechanics that should usually be avoided for this brand."),
    rationale: z
      .string()
      .trim()
      .min(1)
      .max(1200)
      .describe("Why this social expression fits the brand facts and audience."),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.secondaryStyle === value.primaryStyle) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["secondaryStyle"],
        message: "Secondary style must differ from primary style.",
      });
    }

    assertUniqueSocialMechanics(value.preferredMechanics, ctx, "preferredMechanics");
    assertUniqueSocialMechanics(value.avoidedMechanics, ctx, "avoidedMechanics");

    const preferred = new Set(value.preferredMechanics);
    const overlaps = value.avoidedMechanics.filter((mechanic) => preferred.has(mechanic));
    if (overlaps.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["avoidedMechanics"],
        message: `Avoided mechanics cannot also be preferred: ${overlaps.join(", ")}`,
      });
    }
  });

export const MARKET_MATURITY_LEVELS = [
  "emerging",
  "growing",
  "mature",
  "saturated",
  "declining",
  "unknown",
] as const;

export type MarketMaturityLevel = (typeof MARKET_MATURITY_LEVELS)[number];

export const DIFFERENTIATION_STRATEGIES = [
  "price",
  "experience",
  "niche",
  "innovation",
  "authority",
  "mixed",
] as const;

export type DifferentiationStrategy = (typeof DIFFERENTIATION_STRATEGIES)[number];

export const COMMERCIAL_MOMENTS = [
  "launch",
  "new_product",
  "rebrand",
  "seasonal",
  "evergreen",
  "promotion",
  "unknown",
] as const;

export type CommercialMoment = (typeof COMMERCIAL_MOMENTS)[number];

export const AUDIENCE_AWARENESS_STAGES = [
  "problem-aware",
  "solution-aware",
  "brand-aware",
  "mixed",
  "unknown",
] as const;

export type AudienceAwarenessStage = (typeof AUDIENCE_AWARENESS_STAGES)[number];

export const GEOGRAPHIC_MARKET_SCOPES = ["global", "country", "region"] as const;
export type GeographicMarketScope = (typeof GEOGRAPHIC_MARKET_SCOPES)[number];

export const GEOGRAPHIC_MARKET_CONFIDENCE_LEVELS = ["explicit", "inferred"] as const;
export type GeographicMarketConfidenceLevel = (typeof GEOGRAPHIC_MARKET_CONFIDENCE_LEVELS)[number];

export const geographicMarketSchema = z
  .object({
    scope: z
      .enum(GEOGRAPHIC_MARKET_SCOPES)
      .describe("Whether this market is global, country, or region scoped."),
    countryCode: z
      .string()
      .regex(/^[A-Z]{2}$/)
      .nullable()
      .default(null)
      .describe("ISO 3166-1 alpha-2 country code for country-scoped markets."),
    regionCode: z
      .string()
      .regex(/^([0-9]{3}|[A-Z]{2,3})$/)
      .nullable()
      .default(null)
      .describe("CLDR/UN M49 region code for region-scoped markets, such as 150 or EU."),
    label: z.string().trim().min(1).describe("Human-readable market label."),
    confidence: z
      .enum(GEOGRAPHIC_MARKET_CONFIDENCE_LEVELS)
      .describe("Whether the market was explicitly stated or inferred from available evidence."),
    rationale: z.string().trim().min(1).describe("Why this market applies to the audience."),
  })
  .superRefine((value, ctx) => {
    if (value.scope === "global") {
      if (value.countryCode || value.regionCode) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Global markets must not include countryCode or regionCode.",
          path: [value.countryCode ? "countryCode" : "regionCode"],
        });
      }
      return;
    }

    if (value.scope === "country" && !value.countryCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Country markets require countryCode.",
        path: ["countryCode"],
      });
    }

    if (value.scope === "region" && !value.regionCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Region markets require regionCode.",
        path: ["regionCode"],
      });
    }
  });
export type GeographicMarket = z.infer<typeof geographicMarketSchema>;

export const coreBrandIdentitySchema = z.object({
  brandName: z.string().describe("Official brand or company name."),
  taglineOrSlogan: z.string().describe("Short brand tagline or slogan."),
  mission: z.string().describe("Brand mission statement."),
  vision: z.string().describe("Long-term brand vision."),
  coreValues: z.array(z.string()).min(1).describe("Core principles and values guiding the brand."),
  brandStoryOrigin: z.string().describe("Founding story and brand origin context."),
  uniqueSellingProposition: z
    .string()
    .describe("Unique selling proposition that differentiates the brand."),
  brandPromise: z.string().describe("Main promise made by the brand to customers."),
  brandArchetype: z.string().describe("Brand archetype label(s), e.g. 'The Hero'."),
  keyDifferentiators: z
    .array(z.string())
    .min(1)
    .describe("Distinct capabilities or traits that set the brand apart."),
});

export const brandVoiceCommunicationStyleSchema = z
  .object({
    tone: z
      .enum(BRAND_COMMUNICATION_TONES)
      .describe(
        "Dominant communication tone. Allowed: formal, friendly, witty, authoritative, playful, analytical, empathetic, mixed.",
      ),
    voice: z
      .enum(BRAND_VOICE_ATTRIBUTES)
      .describe(
        "Primary voice attribute. Allowed: confident, playful, analytical, empathetic, professional, approachable, bold, mixed.",
      ),
    vocabularyStyle: z
      .enum(BRAND_VOCABULARY_STYLES)
      .describe("Vocabulary style. Allowed: simple, technical, trendy, aspirational, mixed."),
    sentenceComplexity: z
      .enum(BRAND_SENTENCE_COMPLEXITIES)
      .describe("Typical sentence complexity. Allowed: simple, moderate, complex, mixed."),
    useOfEmoji: z
      .enum(BRAND_EMOJI_USAGES)
      .describe("Emoji usage preference. Allowed: none, rare, occasional, frequent."),
    culturalReferences: z
      .enum(BRAND_CULTURAL_REFERENCE_LEVELS)
      .describe("Cultural references usage level. Allowed: none, light, moderate, high."),
    restrictedTopics: z
      .array(z.string())
      .describe("Topics the brand avoids or forbids in communication."),
    wordsToAvoid: z.array(z.string()).describe("Specific words or terms the brand should avoid."),
    wordsToEmphasize: z
      .array(z.string())
      .describe("Specific words or terms the brand prefers to emphasize."),
    ...brandVoiceContentDnaFieldsShape,
    socialExpression: brandVoiceSocialExpressionSchema
      .nullable()
      .default(null)
      .describe(
        "How the brand should show up in social-native content, folded into Brand Voice rather than stored as a separate brand representation.",
      ),
  })
  .superRefine(assertBrandVoiceContentDnaFieldConsistency);

export const visualIdentitySnapshotSchema = z.object({
  logoUrl: z.string().url().describe("Source URL of the identified logo asset on the origin page."),
  logoStyle: z.string().describe("Logo style descriptor, e.g. minimal, bold, corporate, playful."),
  colorPalette: z
    .array(z.string())
    .min(1)
    .describe("Brand color palette, preferably hex values or named colors."),
  secondaryColorPalette: z
    .array(z.string())
    .min(1)
    .describe(
      "Homepage-derived secondary color palette capturing broader UI colors beyond the logo, preferably hex values.",
    ),
  typographyStyle: z
    .union([typographyStyleClaimSchema, z.string()])
    .describe("Structured typography claim with family usage, roles, and source metadata."),
  visualMood: z.string().describe("Overall visual mood and aesthetic direction."),
  imageStyle: z
    .string()
    .describe("Image style descriptor, e.g. stocky, authentic, cinematic, raw."),
  designConsistencyLevel: z
    .string()
    .describe("How consistent and coherent the visual system appears."),
});

const brandVisualUniverseTextSchema = z.string().trim().min(1);

export const BRAND_VISUAL_UNIVERSE_IMAGE_STUDY_MAX_ITEMS_PER_SECTION = 6;

export const BRAND_VISUAL_UNIVERSE_FIELD_PATHS = [
  "brandVisualUniverse.summary",
  "brandVisualUniverse.sourceLogic",
  "brandVisualUniverse.photographyArchetypes",
  "brandVisualUniverse.environments",
  "brandVisualUniverse.materials",
  "brandVisualUniverse.motifs",
  "brandVisualUniverse.colorRoles",
  "brandVisualUniverse.compositionRules",
  "brandVisualUniverse.promptPatterns",
  "brandVisualUniverse.antiPatterns",
] as const;

export type BrandVisualUniverseFieldPath = (typeof BRAND_VISUAL_UNIVERSE_FIELD_PATHS)[number];

export const BRAND_VISUAL_UNIVERSE_SUMMARY_TEMPLATE = [
  "Source logic: {sourceLogic}.",
  "Photography: {photographyArchetypes}.",
  "Environments: {environments}.",
  "Materials: {materials}.",
  "Motifs: {motifs}.",
  "Color roles: {colorRoles}.",
  "Composition rules: {compositionRules}.",
  "Avoid: {antiPatterns}.",
] as const;

export const BRAND_VISUAL_UNIVERSE_MANUAL_STRATEGY_KEY = "user_visual_universe_edit";
export const BRAND_VISUAL_UNIVERSE_MANUAL_STRATEGY_VERSION = 1;
export const BRAND_ONBOARDING_CONFIRMED_STRATEGY_KEY = "user_confirmed_onboarding";
export const BRAND_ONBOARDING_CONFIRMED_STRATEGY_VERSION = 1;
export const BRAND_PROFILE_EDIT_EVIDENCE_SOURCE_TYPES = [
  "official",
  "external",
  "discussion",
  "user",
  "current_profile",
  "none",
] as const;
export type BrandProfileEditEvidenceSourceType =
  (typeof BRAND_PROFILE_EDIT_EVIDENCE_SOURCE_TYPES)[number];
export const brandProfileEditEvidenceSourceTypeSchema = z.enum(
  BRAND_PROFILE_EDIT_EVIDENCE_SOURCE_TYPES,
);
export const brandProfileEditEvidenceSchema = z
  .object({
    fieldPath: z
      .string()
      .trim()
      .min(1)
      .max(160)
      .describe("Exact Brand Profile field path supported by the evidence."),
    sourceUrl: z.url().describe("Exact public evidence URL for this field."),
    sourceType: brandProfileEditEvidenceSourceTypeSchema.describe(
      "Type of evidence source for this field.",
    ),
    confidence: z.number().min(0).max(1).describe("Confidence in this evidence for the field."),
    note: z
      .string()
      .trim()
      .min(1)
      .max(500)
      .describe("Short explanation of what the URL supports for this field."),
  })
  .strict();
export type BrandProfileEditEvidence = z.infer<typeof brandProfileEditEvidenceSchema>;

export const brandVisualUniversePhotographyArchetypeSchema = z.object({
  name: brandVisualUniverseTextSchema.describe(
    "Short reusable name for the photography archetype.",
  ),
  description: brandVisualUniverseTextSchema.describe(
    "Concrete description of the repeatable photographic world.",
  ),
  subjects: z
    .array(brandVisualUniverseTextSchema)
    .default([])
    .describe("Primary subjects to show."),
  settings: z
    .array(brandVisualUniverseTextSchema)
    .default([])
    .describe("Settings where this archetype naturally appears."),
  lighting: brandVisualUniverseTextSchema.describe("Lighting and color-grade guidance."),
  framing: brandVisualUniverseTextSchema
    .optional()
    .describe("Camera angle, crop, depth, and framing guidance."),
  useWhen: brandVisualUniverseTextSchema.describe("When this archetype should be selected."),
});

export const brandVisualUniverseEnvironmentSchema = z.object({
  name: brandVisualUniverseTextSchema.describe("Short environment name."),
  description: brandVisualUniverseTextSchema.describe(
    "Scene-setting guidance for this environment.",
  ),
  cues: z
    .array(brandVisualUniverseTextSchema)
    .default([])
    .describe("Props, locations, or atmosphere cues."),
});

export const brandVisualUniverseMaterialSchema = z.object({
  name: brandVisualUniverseTextSchema.describe("Material, texture, or surface name."),
  usage: brandVisualUniverseTextSchema.describe("How the material should appear in creative work."),
  sensoryCue: brandVisualUniverseTextSchema.describe(
    "Visual or tactile quality the material should imply.",
  ),
});

export const brandVisualUniverseMotifSchema = z.object({
  name: brandVisualUniverseTextSchema.describe("Reusable motif, shape, gesture, or metaphor."),
  meaning: brandVisualUniverseTextSchema.describe("What the motif communicates for the brand."),
  usage: brandVisualUniverseTextSchema.describe("How to use the motif without making it generic."),
  kind: z
    .enum(["look", "content"])
    .optional()
    .describe(
      "Whether the motif controls visual treatment (look) or prescribes recurring scene content.",
    ),
});

export const brandVisualUniverseColorRoleSchema = z.object({
  name: brandVisualUniverseTextSchema.describe(
    "Functional role name, such as hero accent or calm base.",
  ),
  colors: z
    .array(brandVisualUniverseTextSchema)
    .default([])
    .describe("Hex values or named colors."),
  role: brandVisualUniverseTextSchema.describe("What this color role does in the composition."),
  usageGuidance: brandVisualUniverseTextSchema.describe("Where and how to apply this color role."),
});

export const brandVisualUniversePromptPatternSchema = z.object({
  name: brandVisualUniverseTextSchema.describe("Reusable prompt pattern name."),
  pattern: brandVisualUniverseTextSchema.describe("Template-like prompt structure."),
  example: brandVisualUniverseTextSchema.describe(
    "Concrete example prompt using brand-specific cues.",
  ),
  useWhen: brandVisualUniverseTextSchema.describe("When this prompt pattern is the right fit."),
});

export const brandVisualUniverseSchema = z.object({
  summary: brandVisualUniverseTextSchema.describe(
    "Compact overview of the brand's repeatable visual operating system.",
  ),
  sourceLogic: z
    .array(brandVisualUniverseTextSchema)
    .min(1)
    .describe("Source logic that explains why the visual universe fits the brand."),
  photographyArchetypes: z
    .array(brandVisualUniversePhotographyArchetypeSchema)
    .min(1)
    .describe("Repeatable photography archetypes for generated creative."),
  environments: z
    .array(brandVisualUniverseEnvironmentSchema)
    .min(1)
    .describe("Environment types where the brand naturally lives."),
  materials: z
    .array(brandVisualUniverseMaterialSchema)
    .min(1)
    .describe("Materials, textures, and surfaces that should recur."),
  motifs: z
    .array(brandVisualUniverseMotifSchema)
    .min(1)
    .describe("Motifs, gestures, shapes, and metaphors to reuse."),
  colorRoles: z
    .array(brandVisualUniverseColorRoleSchema)
    .min(1)
    .describe("Functional color roles, not only raw palette values."),
  compositionRules: z
    .array(brandVisualUniverseTextSchema)
    .min(1)
    .describe("Framing, negative-space, hierarchy, and layout rules."),
  promptPatterns: z
    .array(brandVisualUniversePromptPatternSchema)
    .describe(
      "Legacy prompt-pattern history retained for API compatibility; not injected into creative prompts.",
    ),
  antiPatterns: z
    .array(brandVisualUniverseTextSchema)
    .min(1)
    .describe("Concrete visual traps and off-brand treatments to avoid."),
});

const brandVisualLookPhotographyStyleV1Schema = z.object({
  lighting: brandVisualUniverseTextSchema.describe("Lighting and color-grade guidance."),
  framing: brandVisualUniverseTextSchema
    .optional()
    .describe("Camera angle, crop, depth, and framing guidance."),
});

const brandVisualLookMotifV1Schema = brandVisualUniverseMotifSchema.extend({
  kind: z.literal("look"),
});

export const brandVisualLookV1Schema = z.object({
  version: z.literal(1),
  photographyStyles: z.array(brandVisualLookPhotographyStyleV1Schema),
  materials: z.array(brandVisualUniverseMaterialSchema),
  motifs: z.array(brandVisualLookMotifV1Schema),
  colorRoles: z.array(brandVisualUniverseColorRoleSchema),
  compositionRules: z.array(brandVisualUniverseTextSchema),
  antiPatterns: z.array(brandVisualUniverseTextSchema),
});

const competitorTupleSchema = z
  .tuple([
    z.string().trim().min(1).describe("Competitor or substitute name."),
    z.string().trim().url().describe("Official homepage URL for that competitor or substitute."),
  ])
  .describe("Tuple containing [name, absolute URL].");

export const positioningMarketContextSchema = z.object({
  marketCategory: z.string().describe("Primary market category the brand operates in."),
  marketSubcategoryNiche: z
    .string()
    .describe("Sub-category or niche segment within the broader market."),
  currentCommercialMoment: z
    .enum(COMMERCIAL_MOMENTS)
    .describe(
      "The brand's current commercial moment. Allowed: launch, new_product, rebrand, seasonal, evergreen, promotion, unknown.",
    ),
  marketMaturityLevel: z
    .enum(MARKET_MATURITY_LEVELS)
    .describe(
      "Market maturity. Allowed: emerging, growing, mature, saturated, declining, unknown.",
    ),
  perceivedPositioning: z
    .array(z.string())
    .min(1)
    .describe(
      "How the brand is perceived in the market (for example premium, innovative, local, ethical).",
    ),
  directCompetitors: z
    .array(competitorTupleSchema)
    .describe("Direct competitors as tuples: [name, absolute URL]."),
  indirectCompetitors: z
    .array(competitorTupleSchema)
    .describe("Indirect competitors as tuples: [name, absolute URL]."),
  substituteSolutions: z
    .array(competitorTupleSchema)
    .describe("Substitute solutions as tuples: [name, absolute URL]."),
  competitiveAdvantages: z
    .array(z.string())
    .min(1)
    .describe("Main competitive advantages claimed or implied by the brand."),
  competitiveDisadvantages: z
    .array(z.string())
    .min(1)
    .describe("Main competitive disadvantages or limitations visible from evidence."),
  differentiationStrategy: z
    .enum(DIFFERENTIATION_STRATEGIES)
    .describe(
      "Dominant differentiation strategy. Allowed: price, experience, niche, innovation, authority, mixed.",
    ),
});

export const HUMAN_LENS_DEFINITIONS = [
  { name: "Emotion", guidance: "Fear, frustration, excitement, and other felt responses." },
  { name: "Behavior", guidance: "Habits, routines, mistakes, and repeated actions." },
  { name: "Identity", guidance: "Identity groups such as Gen Z, founders, or parents." },
  { name: "Relationships", guidance: "Interpersonal bonds, roles, expectations, and friction." },
  { name: "Aspiration", guidance: "Goals, ambitions, desired futures, and self-improvement." },
  { name: "Culture", guidance: "Shared norms, language, rituals, and cultural context." },
  { name: "Entertainment", guidance: "Humor, play, stories, fandom, and leisure." },
  { name: "Status", guidance: "Recognition, belonging, prestige, and social comparison." },
  { name: "Transformation", guidance: "Meaningful before-and-after change in life or identity." },
  {
    name: "Conflict",
    guidance: "Competing groups or viewpoints, such as rich versus poor or men versus women.",
  },
] as const;

export type HumanLensName = (typeof HUMAN_LENS_DEFINITIONS)[number]["name"];

export const HUMAN_LENS_NAMES = HUMAN_LENS_DEFINITIONS.map((definition) => definition.name) as [
  HumanLensName,
  ...HumanLensName[],
];

export const humanLensNameSchema = z.enum(HUMAN_LENS_NAMES);

export const humanLensSchema = z
  .object({
    name: humanLensNameSchema,
    description: z.string().trim().min(1),
    audienceTension: z.string().trim().min(1),
  })
  .strict();

export const humanLensesSchema = z
  .array(humanLensSchema)
  .length(HUMAN_LENS_NAMES.length)
  .superRefine((lenses, ctx) => {
    const names = lenses.map((lens) => lens.name);
    const uniqueNames = new Set(names);
    if (uniqueNames.size !== names.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Human Lenses must include each canonical lens exactly once.",
      });
    }

    const missingNames = HUMAN_LENS_NAMES.filter((name) => !uniqueNames.has(name));
    if (missingNames.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Missing canonical Human Lenses: ${missingNames.join(", ")}.`,
      });
    }
  });

const legacyHumanLensesSchema = z
  .array(
    z.object({
      name: z.string().trim().min(1),
      description: z.string().trim().min(1),
      audienceTension: z.string().trim().min(1),
    }),
  )
  .min(1);

export const readableHumanLensesSchema = z.union([humanLensesSchema, legacyHumanLensesSchema]);

export const domainTerritorySchema = z
  .object({
    name: z.string().trim().min(1),
    description: z.string().trim().min(1),
  })
  .strict();

export const domainTerritoriesSchema = z
  .array(domainTerritorySchema)
  .min(10)
  .max(20)
  .superRefine((territories, ctx) => {
    const normalizedNames = territories.map((territory) => territory.name.toLocaleLowerCase());
    if (new Set(normalizedNames).size !== normalizedNames.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Domain Territories must have unique names.",
      });
    }
  });

const readableDomainTerritoriesSchema = z
  .array(
    z.object({
      name: z.string().trim().min(1),
      description: z.string().trim().min(1),
    }),
  )
  .min(10)
  .max(20)
  .superRefine((territories, ctx) => {
    const normalizedNames = territories.map((territory) => territory.name.toLocaleLowerCase());
    if (new Set(normalizedNames).size !== normalizedNames.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Domain Territories must have unique names.",
      });
    }
  });

export const targetAudienceSchema = z.object({
  primaryAudienceSegments: z.array(z.string()).min(1).describe("Primary target audience segments."),
  secondaryAudienceSegments: z.array(z.string()).describe("Secondary audience segments."),
  demographics: z
    .string()
    .describe("Demographic summary (age, gender, location, income, education) when inferable."),
  psychographics: z.string().describe("Psychographic summary (values, beliefs, lifestyle)."),
  behavioralTraits: z
    .string()
    .describe("Behavioral traits summary (buying habits, channels used, engagement patterns)."),
  painPoints: z
    .array(z.string())
    .min(1)
    .describe("Key pain points the audience is trying to solve."),
  desiresAspirations: z
    .array(z.string())
    .min(1)
    .describe("Audience desires, aspirations, or outcomes they seek."),
  buyingTriggers: z
    .array(z.string())
    .min(1)
    .describe("Events or factors that trigger buying intent."),
  awarenessStage: z
    .enum(AUDIENCE_AWARENESS_STAGES)
    .describe(
      "Audience awareness stage. Allowed: problem-aware, solution-aware, brand-aware, mixed, unknown.",
    ),
  decisionMakingDrivers: z
    .array(z.string())
    .min(1)
    .describe("Top decision-making drivers (for example price, trust, speed, status)."),
  geographicMarkets: z
    .array(geographicMarketSchema)
    .default([])
    .describe(
      "Audience countries, regions, or global scope. Campaigns should choose the specific markets they address from this audience geography.",
    ),
  audienceProfileSummary: z
    .string()
    .describe("Evidence-backed synthesis of the real audience, grounded in public conversation.")
    .optional(),
  redditConversationSignals: z
    .array(
      z.object({
        topic: z.string().min(1),
        subreddit: z.string().min(1),
        signal: z.string().min(1),
        audienceLanguage: z.array(z.string()).default([]),
        sourceUrl: z.string().url(),
      }),
    )
    .min(1)
    .describe(
      "Reddit-sourced conversation signals that reveal audience beliefs, pains, or language.",
    )
    .optional(),
  brandConversationSignals: z
    .array(
      z.object({
        topic: z.string().min(1),
        signal: z.string().min(1),
        audienceLanguage: z.array(z.string()).default([]),
        sourceUrl: z.string().url(),
      }),
    )
    .default([])
    .describe("Brand-specific public conversation signals when available.")
    .optional(),
  painPointMap: z
    .array(
      z.object({
        painPoint: z.string().min(1),
        audienceSegment: z.string().min(1),
        emotionalRoot: z.string().min(1),
        awkwardness: z.string().min(1),
        evidence: z.array(z.string()).min(1),
      }),
    )
    .min(1)
    .describe(
      "Pain points mapped to who feels them and the awkward or emotional roots behind them.",
    )
    .optional(),
  resonanceArchetypeScores: z
    .array(
      z.object({
        archetype: z.string().min(1),
        score: z.number().min(0).max(100),
        rationale: z.string().min(1),
      }),
    )
    .min(1)
    .describe("Audience resonance/archetype scores from 0-100 with rationale.")
    .optional(),
  humanLenses: readableHumanLensesSchema
    .describe(
      "The ten canonical Human Lenses, each filled with an audience-specific description and tension.",
    )
    .optional(),
  domainTerritories: readableDomainTerritoriesSchema
    .describe(
      "10-20 adjacent domains the audience naturally cares about: topics they already consume, not product features or company messaging.",
    )
    .optional(),
  postTypeResonance: z
    .object({
      branded: z.array(z.string()).min(1),
      unbranded: z.array(z.string()).min(1),
      adjacent: z.array(z.string()).min(1),
    })
    .describe(
      "Branded, unbranded, and adjacent post patterns likely to resonate with the audience.",
    )
    .optional(),
});

export const brandProfileSchema = z.object({
  coreIdentity: coreBrandIdentitySchema,
  brandVoiceCommunicationStyle: brandVoiceCommunicationStyleSchema,
  positioningMarketContext: positioningMarketContextSchema,
  targetAudience: targetAudienceSchema,
  visualIdentitySnapshot: visualIdentitySnapshotSchema,
});

export type CoreBrandIdentity = z.infer<typeof coreBrandIdentitySchema>;
export type BrandVoiceCommunicationStyle = z.infer<typeof brandVoiceCommunicationStyleSchema>;
export type BrandVoiceSocialExpression = z.infer<typeof brandVoiceSocialExpressionSchema>;
export type PositioningMarketContext = z.infer<typeof positioningMarketContextSchema>;
export type TargetAudience = z.infer<typeof targetAudienceSchema>;
export type VisualIdentitySnapshot = z.infer<typeof visualIdentitySnapshotSchema>;
export type BrandVisualUniverse = z.infer<typeof brandVisualUniverseSchema>;
export type BrandVisualLookV1 = z.infer<typeof brandVisualLookV1Schema>;
export type BrandVisualUniverseSummarySource = Omit<BrandVisualUniverse, "summary">;
export type BrandProfile = z.infer<typeof brandProfileSchema>;
export type BrandFactClaimValue =
  | string
  | string[]
  | string[][]
  | Record<string, unknown>
  | Record<string, unknown>[];

export type BrandVisualUniverseFactValueMap = Record<
  BrandVisualUniverseFieldPath,
  BrandFactClaimValue
>;

export type BrandVisualUniverseImageStudyItem = {
  section: BrandVisualUniverseImageSection;
  itemName: string;
  itemIndex: number;
};

const BRAND_VISUAL_UNIVERSE_SUMMARY_MAX_ITEMS_PER_SECTION = 2;

const normalizeBrandVisualUniverseSummaryCue = (value: string): string =>
  value
    .replace(/\s+/g, " ")
    .replace(/[.!?;:,]+$/g, "")
    .trim();

const selectBrandVisualUniverseSummaryCues = (values: readonly string[]): string[] =>
  values
    .slice(0, BRAND_VISUAL_UNIVERSE_SUMMARY_MAX_ITEMS_PER_SECTION)
    .map(normalizeBrandVisualUniverseSummaryCue);

const formatBrandVisualUniverseSummaryNames = (values: readonly string[]): string => {
  const selected = selectBrandVisualUniverseSummaryCues(values);
  return selected.length === 2 ? `${selected[0]} and ${selected[1]}` : (selected[0] ?? "");
};

const formatBrandVisualUniverseSummaryProse = (values: readonly string[]): string =>
  selectBrandVisualUniverseSummaryCues(values).join("; ");

export const composeBrandVisualUniverseSummary = (
  visualUniverse: BrandVisualUniverseSummarySource,
): string => {
  const replacements = {
    sourceLogic: formatBrandVisualUniverseSummaryProse(visualUniverse.sourceLogic),
    photographyArchetypes: formatBrandVisualUniverseSummaryNames(
      visualUniverse.photographyArchetypes.map((item) => item.name),
    ),
    environments: formatBrandVisualUniverseSummaryNames(
      visualUniverse.environments.map((item) => item.name),
    ),
    materials: formatBrandVisualUniverseSummaryNames(
      visualUniverse.materials.map((item) => item.name),
    ),
    motifs: formatBrandVisualUniverseSummaryNames(visualUniverse.motifs.map((item) => item.name)),
    colorRoles: formatBrandVisualUniverseSummaryNames(
      visualUniverse.colorRoles.map((item) => item.name),
    ),
    compositionRules: formatBrandVisualUniverseSummaryProse(visualUniverse.compositionRules),
    antiPatterns: formatBrandVisualUniverseSummaryProse(visualUniverse.antiPatterns),
  };

  return BRAND_VISUAL_UNIVERSE_SUMMARY_TEMPLATE.map((sentence) =>
    Object.entries(replacements).reduce<string>(
      (composed, [key, value]) => composed.replace(`{${key}}`, value),
      sentence,
    ),
  ).join(" ");
};

const toBrandVisualUniverseRecordArray = <T extends object>(
  items: readonly T[],
): Record<string, unknown>[] =>
  items.map((item) => Object.fromEntries(Object.entries(item)) as Record<string, unknown>);

export const brandVisualUniverseFieldKey = (fieldPath: BrandVisualUniverseFieldPath): string =>
  fieldPath.replace("brandVisualUniverse.", "");

export const toBrandVisualUniverseFactValueMap = (
  visualUniverse: BrandVisualUniverse,
): BrandVisualUniverseFactValueMap => ({
  "brandVisualUniverse.summary": visualUniverse.summary,
  "brandVisualUniverse.sourceLogic": visualUniverse.sourceLogic,
  "brandVisualUniverse.photographyArchetypes": toBrandVisualUniverseRecordArray(
    visualUniverse.photographyArchetypes,
  ),
  "brandVisualUniverse.environments": toBrandVisualUniverseRecordArray(visualUniverse.environments),
  "brandVisualUniverse.materials": toBrandVisualUniverseRecordArray(visualUniverse.materials),
  "brandVisualUniverse.motifs": toBrandVisualUniverseRecordArray(visualUniverse.motifs),
  "brandVisualUniverse.colorRoles": toBrandVisualUniverseRecordArray(visualUniverse.colorRoles),
  "brandVisualUniverse.compositionRules": visualUniverse.compositionRules,
  "brandVisualUniverse.promptPatterns": toBrandVisualUniverseRecordArray(
    visualUniverse.promptPatterns,
  ),
  "brandVisualUniverse.antiPatterns": visualUniverse.antiPatterns,
});

export const limitBrandVisualUniverseImageStudyItems = <T>(items: readonly T[]): T[] =>
  items.slice(0, BRAND_VISUAL_UNIVERSE_IMAGE_STUDY_MAX_ITEMS_PER_SECTION);

export const getExpectedBrandVisualUniverseImageItems = (
  visualUniverse: BrandVisualUniverse,
): BrandVisualUniverseImageStudyItem[] => [
  ...limitBrandVisualUniverseImageStudyItems(visualUniverse.photographyArchetypes).map(
    (item, itemIndex) => ({
      section: "photography_archetype" as const,
      itemName: item.name,
      itemIndex,
    }),
  ),
  ...limitBrandVisualUniverseImageStudyItems(visualUniverse.environments).map(
    (item, itemIndex) => ({
      section: "environment" as const,
      itemName: item.name,
      itemIndex,
    }),
  ),
  ...limitBrandVisualUniverseImageStudyItems(visualUniverse.materials).map((item, itemIndex) => ({
    section: "material" as const,
    itemName: item.name,
    itemIndex,
  })),
  ...limitBrandVisualUniverseImageStudyItems(visualUniverse.motifs).map((item, itemIndex) => ({
    section: "motif" as const,
    itemName: item.name,
    itemIndex,
  })),
];

export const getBrandVisualUniverseImageStudyItemKey = (
  item: BrandVisualUniverseImageStudyItem,
): string => `${item.section}\0${item.itemIndex}\0${item.itemName}`;

export interface FormatBrandVisualLookForPromptOptions {
  maxItemsPerSection?: number;
  maxTextLength?: number;
}

/** @deprecated Use FormatBrandVisualLookForPromptOptions. */
export interface FormatBrandVisualUniverseForPromptOptions extends FormatBrandVisualLookForPromptOptions {}

const compactPromptText = (value: string, maxLength: number): string => {
  const normalized = value.replace(/\s+/g, " ").trim();
  if (normalized.length <= maxLength) {
    return normalized;
  }

  return `${normalized.slice(0, Math.max(1, maxLength - 3)).trimEnd()}...`;
};

export const parseBrandVoiceSocialExpression = (
  value: unknown,
): BrandVoiceSocialExpression | null => {
  const parsed = brandVoiceSocialExpressionSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
};

export const mapLegacyBrandHumorUsageToSocialIntensity = (
  legacyHumorUsage: unknown,
): BrandSocialIntensityLevel | null => {
  switch (legacyHumorUsage) {
    case "none":
    case "subtle":
      return "low";
    case "moderate":
      return "medium";
    case "strong":
      return "high";
    default:
      return null;
  }
};

export interface FormatBrandVoiceSocialExpressionForPromptOptions {
  maxMechanics?: number;
  maxTextLength?: number;
}

const resolvePositiveIntegerOption = (
  value: number | undefined,
  fallback: number,
  minimum: number,
): number => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return fallback;
  }

  return Math.max(minimum, Math.trunc(value));
};

export const formatBrandVoiceSocialExpressionForPrompt = (
  value: unknown,
  options: FormatBrandVoiceSocialExpressionForPromptOptions = {},
): string[] => {
  const socialExpression = parseBrandVoiceSocialExpression(value);
  if (!socialExpression) {
    return [];
  }

  const maxMechanics = resolvePositiveIntegerOption(options.maxMechanics, 6, 1);
  const maxTextLength = resolvePositiveIntegerOption(options.maxTextLength, 220, 40);
  const compact = (text: string) => compactPromptText(text, maxTextLength);
  const secondaryStyle = socialExpression.secondaryStyle
    ? `, secondary ${socialExpression.secondaryStyle}`
    : "";
  const preferredMechanics = socialExpression.preferredMechanics.slice(0, maxMechanics).join(", ");
  const avoidedMechanics = socialExpression.avoidedMechanics.slice(0, maxMechanics).join(", ");

  return [
    `Primary social style: ${socialExpression.primaryStyle}${secondaryStyle}; confidence ${socialExpression.confidence}.`,
    `Expression levels: opinionatedness ${socialExpression.opinionatedness}, aspiration ${socialExpression.aspirationLevel}, education ${socialExpression.educationLevel}, community ${socialExpression.communityLevel}.`,
    `Preferred mechanics: ${preferredMechanics}.`,
    avoidedMechanics ? `Avoid mechanics: ${avoidedMechanics}.` : null,
    `Rationale: ${compact(socialExpression.rationale)}`,
  ].filter((line): line is string => !!line);
};

export interface FormatBrandVoiceContentDnaForPromptOptions {
  maxItems?: number;
  maxTextLength?: number;
}

export const parseBrandVoiceContentDnaFields = (value: unknown): BrandVoiceContentDnaFields =>
  brandVoiceContentDnaFieldsSchema.parse(value ?? {});

const hasParsedBrandVoiceContentDnaFields = (contentDna: BrandVoiceContentDnaFields): boolean =>
  Boolean(
    contentDna.contentDnaPrimaryStyle ||
    contentDna.contentDnaSecondaryStyle ||
    contentDna.contentDnaConfidence ||
    contentDna.contentDnaHumorLevel ||
    contentDna.contentDnaOpinionatedness ||
    contentDna.contentDnaAspirationLevel ||
    contentDna.contentDnaEducationLevel ||
    contentDna.contentDnaCommunityLevel ||
    contentDna.socialMechanics.length > 0 ||
    contentDna.socialAvoidedMechanics.length > 0 ||
    contentDna.socialTruths.length > 0 ||
    contentDna.audienceTensions.length > 0 ||
    contentDna.shareabilityTriggers.length > 0 ||
    contentDna.contentAngles.length > 0 ||
    contentDna.contentAntiPatterns.length > 0 ||
    contentDna.contentDnaRationale,
  );

export const hasBrandVoiceContentDnaFields = (value: unknown): boolean => {
  const parsed = brandVoiceContentDnaFieldsSchema.safeParse(value ?? {});
  if (!parsed.success) {
    return false;
  }

  return hasParsedBrandVoiceContentDnaFields(parsed.data);
};

export const formatBrandVoiceContentDnaForPrompt = (
  value: unknown,
  options: FormatBrandVoiceContentDnaForPromptOptions = {},
): string[] => {
  const parsed = brandVoiceContentDnaFieldsSchema.safeParse(value ?? {});
  if (!parsed.success || !hasParsedBrandVoiceContentDnaFields(parsed.data)) {
    return [];
  }

  const contentDna = parsed.data;
  const maxItems = resolvePositiveIntegerOption(options.maxItems, 5, 1);
  const maxTextLength = resolvePositiveIntegerOption(options.maxTextLength, 220, 40);
  const compact = (text: string) => compactPromptText(text, maxTextLength);
  const list = (values: readonly string[]) => values.slice(0, maxItems).map(compact).join("; ");
  const secondaryStyle = contentDna.contentDnaSecondaryStyle
    ? `, secondary ${contentDna.contentDnaSecondaryStyle}`
    : "";
  const levelParts = [
    contentDna.contentDnaHumorLevel ? `humor ${contentDna.contentDnaHumorLevel}` : null,
    contentDna.contentDnaOpinionatedness
      ? `opinionatedness ${contentDna.contentDnaOpinionatedness}`
      : null,
    contentDna.contentDnaAspirationLevel
      ? `aspiration ${contentDna.contentDnaAspirationLevel}`
      : null,
    contentDna.contentDnaEducationLevel ? `education ${contentDna.contentDnaEducationLevel}` : null,
    contentDna.contentDnaCommunityLevel ? `community ${contentDna.contentDnaCommunityLevel}` : null,
  ].filter((part): part is string => !!part);

  return [
    contentDna.contentDnaPrimaryStyle
      ? `Content DNA: primary ${contentDna.contentDnaPrimaryStyle}${secondaryStyle}${contentDna.contentDnaConfidence ? `; confidence ${contentDna.contentDnaConfidence}` : ""}.`
      : null,
    levelParts.length ? `Content DNA levels: ${levelParts.join(", ")}.` : null,
    contentDna.socialMechanics.length
      ? `Preferred social mechanics: ${contentDna.socialMechanics.slice(0, maxItems).join(", ")}.`
      : null,
    contentDna.socialAvoidedMechanics.length
      ? `Avoid social mechanics: ${contentDna.socialAvoidedMechanics.slice(0, maxItems).join(", ")}.`
      : null,
    contentDna.socialTruths.length ? `Social truths: ${list(contentDna.socialTruths)}.` : null,
    contentDna.audienceTensions.length
      ? `Audience tensions: ${list(contentDna.audienceTensions)}.`
      : null,
    contentDna.shareabilityTriggers.length
      ? `Shareability triggers: ${list(contentDna.shareabilityTriggers)}.`
      : null,
    contentDna.contentAngles.length ? `Content angles: ${list(contentDna.contentAngles)}.` : null,
    contentDna.contentAntiPatterns.length
      ? `Content anti-patterns: ${list(contentDna.contentAntiPatterns)}.`
      : null,
    contentDna.contentDnaRationale
      ? `Content DNA rationale: ${compact(contentDna.contentDnaRationale)}`
      : null,
  ].filter((line): line is string => !!line);
};

const formatPromptStringList = (
  values: readonly string[],
  maxItems: number,
  maxTextLength: number,
): string | null => {
  const items = values
    .map((value) => compactPromptText(value, maxTextLength))
    .filter((value) => value.length > 0)
    .slice(0, maxItems);

  return items.length > 0 ? items.join(", ") : null;
};

const formatPromptSection = <T>(
  values: readonly T[],
  maxItems: number,
  formatter: (value: T) => string,
): string | null => {
  const formatted = values
    .slice(0, maxItems)
    .map(formatter)
    .filter((value) => value.trim().length > 0);

  return formatted.length > 0 ? formatted.join(" / ") : null;
};

export const parseBrandVisualUniverse = (value: unknown): BrandVisualUniverse | null => {
  const parsed = brandVisualUniverseSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
};

const isBrandVisualLookSourceRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

const projectBrandVisualLookArray = <T>(value: unknown, schema: z.ZodType<T>): T[] =>
  Array.isArray(value)
    ? value.flatMap((item) => {
        const parsed = schema.safeParse(item);
        return parsed.success ? [parsed.data] : [];
      })
    : [];

const projectBrandVisualLookTextList = (value: unknown): string[] =>
  projectBrandVisualLookArray(value, brandVisualUniverseTextSchema);

const projectBrandVisualLookPhotographyStyles = (
  value: unknown,
): BrandVisualLookV1["photographyStyles"] =>
  Array.isArray(value)
    ? value.flatMap((candidate) => {
        if (!isBrandVisualLookSourceRecord(candidate)) return [];
        const lighting = brandVisualUniverseTextSchema.safeParse(candidate.lighting);
        if (!lighting.success) return [];
        const framing = brandVisualUniverseTextSchema.safeParse(candidate.framing);
        return [
          {
            lighting: lighting.data,
            ...(framing.success ? { framing: framing.data } : {}),
          },
        ];
      })
    : [];

/**
 * Projects a persisted Visual Universe (or an already projected look) onto the
 * prompt-safe visual-treatment contract. Every field and item is parsed
 * independently so one malformed or absent legacy field cannot discard valid
 * look guidance from another field.
 */
export const projectBrandVisualUniverseToLook = (value: unknown): BrandVisualLookV1 => {
  const source = isBrandVisualLookSourceRecord(value) ? value : {};
  const photographySource = Array.isArray(source.photographyStyles)
    ? source.photographyStyles
    : source.photographyArchetypes;

  return brandVisualLookV1Schema.parse({
    version: 1,
    photographyStyles: projectBrandVisualLookPhotographyStyles(photographySource),
    materials: projectBrandVisualLookArray(source.materials, brandVisualUniverseMaterialSchema),
    motifs: projectBrandVisualLookArray(source.motifs, brandVisualLookMotifV1Schema),
    colorRoles: projectBrandVisualLookArray(source.colorRoles, brandVisualUniverseColorRoleSchema),
    compositionRules: projectBrandVisualLookTextList(source.compositionRules),
    antiPatterns: projectBrandVisualLookTextList(source.antiPatterns),
  });
};

export const formatBrandVisualLookForPrompt = (
  value: unknown,
  options: FormatBrandVisualLookForPromptOptions = {},
): string[] => {
  const visualLook = projectBrandVisualUniverseToLook(value);
  const maxItems = resolvePositiveIntegerOption(options.maxItemsPerSection, 3, 1);
  const maxTextLength = resolvePositiveIntegerOption(options.maxTextLength, 220, 40);
  const compact = (text: string) => compactPromptText(text, maxTextLength);
  const list = (values: readonly string[], itemLimit = maxItems) =>
    formatPromptStringList(values, itemLimit, maxTextLength);
  const labeledList = (label: string, values: readonly string[], itemLimit = maxItems) => {
    const formatted = list(values, itemLimit);
    return formatted ? `${label} ${formatted}` : null;
  };

  const photography = formatPromptSection(visualLook.photographyStyles, maxItems, (item) =>
    [`lighting ${item.lighting}`, item.framing ? `framing ${item.framing}` : null]
      .filter((part): part is string => !!part)
      .map(compact)
      .join("; "),
  );
  const materials = formatPromptSection(visualLook.materials, maxItems, (item) =>
    [item.name, item.usage, item.sensoryCue].map(compact).join("; "),
  );
  const motifs = formatPromptSection(visualLook.motifs, maxItems, (item) =>
    [item.name, item.meaning, item.usage].map(compact).join("; "),
  );
  const colorRoles = formatPromptSection(visualLook.colorRoles, maxItems, (item) =>
    [item.name, labeledList("colors", item.colors, 4), item.role, item.usageGuidance]
      .filter((part): part is string => !!part)
      .map(compact)
      .join("; "),
  );
  const compositionRules = list(visualLook.compositionRules);
  const antiPatterns = list(visualLook.antiPatterns);

  return [
    photography ? `Photography styles: ${photography}` : null,
    materials ? `Materials: ${materials}` : null,
    motifs ? `Motifs: ${motifs}` : null,
    colorRoles ? `Color roles: ${colorRoles}` : null,
    compositionRules ? `Composition rules: ${compositionRules}` : null,
    antiPatterns ? `Anti-patterns: ${antiPatterns}` : null,
  ].filter((line): line is string => !!line);
};

/**
 * @deprecated Visual Universe scene content is not safe for creative prompts.
 * Use formatBrandVisualLookForPrompt instead.
 */
export const formatBrandVisualUniverseForPrompt = (
  value: unknown,
  options: FormatBrandVisualUniverseForPromptOptions = {},
): string[] => formatBrandVisualLookForPrompt(value, options);

export interface BrandProfileFieldHint {
  path: string;
  description: string;
  valueType: "string" | "string[]" | "json";
}

const unwrapSchema = (schema: z.ZodType): z.ZodType => {
  if (schema instanceof z.ZodArray) {
    return schema;
  }

  if ("unwrap" in schema && typeof schema.unwrap === "function") {
    return unwrapSchema(schema.unwrap() as z.ZodType);
  }
  return schema;
};

const inferValueType = (schema: z.ZodType): BrandProfileFieldHint["valueType"] => {
  const unwrapped = unwrapSchema(schema);

  if (unwrapped instanceof z.ZodArray) {
    const element = (unwrapped as unknown as { element: z.ZodType }).element;
    const unwrappedElement = unwrapSchema(element);

    if (unwrappedElement instanceof z.ZodString || unwrappedElement instanceof z.ZodEnum) {
      return "string[]";
    }

    return "json";
  }

  if (unwrapped instanceof z.ZodObject) {
    return "json";
  }

  if (unwrapped instanceof z.ZodUnion) {
    const options = (unwrapped as unknown as { options: z.ZodType[] }).options.map((option) =>
      unwrapSchema(option),
    );
    if (options.some((option) => option instanceof z.ZodArray)) {
      const hasJsonArray = options
        .filter((option) => option instanceof z.ZodArray)
        .some((option) => inferValueType(option) === "json");
      return hasJsonArray ? "json" : "string[]";
    }
    if (options.some((option) => option instanceof z.ZodObject)) {
      return "json";
    }
  }

  return "string";
};

/**
 * Introspected ranking hints derived from the canonical Zod schema.
 * The worker uses this to explain URL ranking criteria to the LLM.
 */
export const getBrandProfileFieldHints = (): BrandProfileFieldHint[] => {
  const sectionSchemas = {
    coreIdentity: coreBrandIdentitySchema,
    brandVoiceCommunicationStyle: brandVoiceCommunicationStyleSchema,
    positioningMarketContext: positioningMarketContextSchema,
    targetAudience: targetAudienceSchema,
    visualIdentitySnapshot: visualIdentitySnapshotSchema,
  } as const;

  const hints: BrandProfileFieldHint[] = [];

  for (const [sectionKey, sectionSchema] of Object.entries(sectionSchemas)) {
    for (const [fieldKey, fieldSchema] of Object.entries(sectionSchema.shape)) {
      const schema = fieldSchema as z.ZodType;
      hints.push({
        path: `${sectionKey}.${fieldKey}`,
        description: schema.description ?? "",
        valueType: inferValueType(schema),
      });
    }
  }

  return hints;
};

export const getCoreIdentityFieldHints = (): BrandProfileFieldHint[] =>
  getBrandProfileFieldHints().filter((hint) => hint.path.startsWith("coreIdentity."));

export const getBrandVoiceCommunicationStyleFieldHints = (): BrandProfileFieldHint[] =>
  getBrandProfileFieldHints().filter((hint) =>
    hint.path.startsWith("brandVoiceCommunicationStyle."),
  );

export const getPositioningMarketContextFieldHints = (): BrandProfileFieldHint[] =>
  getBrandProfileFieldHints().filter((hint) => hint.path.startsWith("positioningMarketContext."));

export const getTargetAudienceFieldHints = (): BrandProfileFieldHint[] =>
  getBrandProfileFieldHints().filter((hint) => hint.path.startsWith("targetAudience."));

export const getVisualIdentityFieldHints = (): BrandProfileFieldHint[] =>
  getBrandProfileFieldHints().filter((hint) => hint.path.startsWith("visualIdentitySnapshot."));

export const getBrandVisualUniverseFieldHints = (): BrandProfileFieldHint[] =>
  Object.entries(brandVisualUniverseSchema.shape).map(([fieldKey, fieldSchema]) => {
    const schema = fieldSchema as z.ZodType;
    return {
      path: `brandVisualUniverse.${fieldKey}`,
      description: schema.description ?? "",
      valueType: inferValueType(schema),
    };
  });
